import { useEffect, useRef } from 'react';
import {
  Router,
  Switch,
  Route,
  Redirect,
  useLocation,
} from 'react-router-dom';
import TagManager from 'react-gtm-module';
import { PostHogProvider } from 'posthog-js/react';
import history from './history';
import './App.css';
import './scss/style.scss';
import { FirebaseProvider } from './components/global/FirebaseProvider/FirebaseProvider';
import PublicRoute from './components/global/Routes/PublicRoute';
import AlertProvider from './components/providers/AlertProvider';
import RedemptionLandingPage from './components/trashie/views/Register';
import { Subscription } from './components/trashie/views/Subscription';
import OneTimeOrder from './components/trashie/views/OneTimeOrder';

// Trashie
import {
  ROUTE_HOME,
  ROUTE_LOGIN,
  ROUTE_EMBED_LOGIN,
  ROUTE_LOGOUT,
  ROUTE_SIGNUP,
  ROUTE_SETTINGS,
  ROUTE_ADDRESSES,
  ROUTE_DASHBOARD,
  ROUTE_REDEEM,
  ROUTE_PROFILE,
  ROUTE_REGISTER,
  ROUTE_WALLET,
  ROUTE_TAKE_BACK_BAGS,
  ROUTE_TAKE_BACK_BAGS_DETAILS,
  ROUTE_MY_ACCOUNT,
  ROUTE_WEB_NOTIFICATIONS_INBOX,
  ROUTE_WEB_NOTIFICATIONS_INBOX_ACCOUNT,
  ROUTE_REWARD_ID,
  ROUTE_REWARD_INFO,
  ROUTE_DEAL_INFO,
  ROUTE_REWARDS_STORE_ID,
  ROUTE_REWARDS_STORE_REWARD_ID,
  ROUTE_REWARDS,
  ROUTE_TRANSACTIONS,
  ROUTE_OFFERS,
  ROUTE_REWARDS_OLD_STORE_ID,
  ROUTE_REWARDS_OLD_STORE_REWARD_ID,
  ROUTE_REWARD_OLD_INFO,
  ROUTE_DEALS,
  ROUTE_DEAL_STORE_ID,
  ROUTE_DEAL_STORE_REWARD_ID,
  ROUTE_GIFT_CARDS,
  ROUTE_GIFT_CARDS_BRAND_ID,
  ROUTE_GIFT_CARD_INFO,
  ROUTE_DASHBOARD_REWARDS,
  ROUTE_SHARE_STORE_ID,
  ROUTE_SHARE_STORE_DEAL_ID,
  ROUTE_EMBED_BROWN_FORM,
  ROUTE_REWARDS_V2,
  ROUTE_REWARDS_V2_LIST,
  ROUTE_REWARDS_V2_STORE_ID,
  ROUTE_REWARDS_V2_STORE_REWARD_ID,
  ROUTE_GIFT_CARDS_V2,
  ROUTE_GIFT_CARDS_V2_BRAND_ID,
  ROUTE_GIFT_CARDS_V2_LIST,
  ROUTE_OFFERS_V2,
  ROUTE_MEMBERSHIP_ADDRESSES,
  ROUTE_MANAGE_MEMBERSHIP,
  ROUTE_SUBSCRIPTION,
  ROUTE_UNLIMITED,
  ROUTE_TRASHIE_UNLIMITED,
  ROUTE_MEMBERSHIP_ORDERS,
  ROUTE_MEMBERSHIP_PAYMENT_METHOD,
  ROUTE_MEMBERSHIP,
  ROUTE_MEMBERSHIP_CANCEL,
  ROUTE_ONE_TIME_ORDER,
  ROUTE_MOBILE_LOGIN,
  ROUTE_MOBILE_VERIFY_EMAIL,
  ROUTE_WELCOME_BACK,
} from './components/trashie/config/routes';
import {
  Container,
  TrashieContainer,
  TrashieContainerPublic,
  TrashieContainerPublicCentered,
  TrashieMembershipContainer,
} from './components/trashie/containers';
import TrashieLogin from './components/trashie/views/Login';
import TrashieEmbedLogin from './components/trashie/views/Embed/Login';
import TrashieLogout from './components/trashie/views/Logout';
import TrashieSettings from './components/trashie/views/Settings';
import TrashieProfile from './components/trashie/views/Profile';
import TrashieAddresses from './components/trashie/views/Addresses';
import TrashieDashboardNew from './components/trashie/views/DashboardDeals';
import TrashieDashboardRewards from './components/trashie/views/DashboardRewards';
import TrashieWallet from './components/trashie/views/Wallet';
import TrashieTakeBackBags from './components/trashie/views/TakeBackBags';
import TrashieTakeBackBagsDetails from './components/trashie/views/TakeBackBagDetails';
import TrashieMyAccount from './components/trashie/views/MyAccount';
import NotificationsInboxWebRedirect from './components/trashie/views/NotificationsInboxWebRedirect/NotificationsInboxWebRedirect';
import TrashieOldReward from './components/trashie/views/OldReward';
import TrashieReward from './components/trashie/views/Reward';
import TrashieRewards from './components/trashie/views/Rewards';
import TrashieRewardInfo from './components/trashie/views/RewardInfo';
import TrashieTransactions from './components/trashie/views/Transactions';
import TrashieOffers from './components/trashie/views/Offers';
import TrashiePublicRoute from './components/trashie/global/Routes/PublicRoute';
import TrashiePrivateRoute from './components/trashie/global/Routes/PrivateRoute';
import EnvironmentBanner from './components/trashie/global/EnvironmentBanner';
import TrashieDeals from './components/trashie/views/Deals';
import TrashieGiftCards from './components/trashie/views/GiftCards';
import TrashieGiftCard from './components/trashie/views/GiftCardBrand';
import TrashieGiftCardInfo from './components/trashie/views/GiftCardInfo';

import TrashieDashboardRewardsV2 from './components/trashie/views/DashboardRewardsV2';
import TrashieDashboardGiftCardsV2 from './components/trashie/views/DashboardGiftCardsV2';
import TrashieRewardsV2List from './components/trashie/views/RewardsV2';
import TrashieGiftCardsV2List from './components/trashie/views/GiftCardsV2';
import TrashieOffersV2 from './components/trashie/views/OffersV2';
import TrashieMembershipOrders from './components/trashie/views/MembershipOrders';
import MembershipPaymentMethod from './components/trashie/views/MembershipPaymentMethod';
import TrashieMembership from './components/trashie/views/MembershipDashboard';
import TrashieMembershipCancel from './components/trashie/views/CancelSubscription';

import { getConfig, isProd, isTrashieApp } from './config/config';
import useScript from './hooks/useScript';
import useExternalScript from './hooks/useExternalScript';
import gaScript from './components/trashie/scripts/ga';
import northbeamScript from './components/trashie/scripts/northbeam';
import equalwebScript from './components/trashie/scripts/equalweb';
import redditPixelScript from './components/trashie/scripts/redditPixel';
import googlePlacesApi from './components/trashie/scripts/googlePlacesApi';
import tiktokPixelScript from './components/trashie/scripts/tiktokPixel';
import magellanPixelScript, { trackMagellanPageView } from './components/trashie/scripts/magellanPixel';

import {
  TRASHIE_POSTHOG_API_HOST,
  TRASHIE_POSTHOG_API_KEY,
  TRASHIE_GA_CONTAINER_ID,
} from './components/trashie/config/config';
import { GOOGLE_TAG_MANAGER_URL } from './components/trashie/config/externalURL';
import { setDocumentFavicon, setDocumentTitle } from './components/trashie/utils/utils';

import MaintenanceBanner from './components/trashie/global/MaintenanceBanner';
import AnalyticsInitializer from './components/trashie/global/AnalyticsInitializer';
import { createBeforeSendHook } from './components/trashie/utils/analytics';
import BrownForm from './components/trashie/views/Embed/BrownForm';
import MembershipAddresses from './components/trashie/views/MembershipAddresses';
import TrashieManageMembership from './components/trashie/views/ManageMembership';
import ShareDeal from './components/trashie/views/ShareDeal';
import MobileRedirect from './components/trashie/views/MobileRedirect';
import UtmProvider from './components/trashie/providers/UtmProvider';

const isFirstPartyEmbed = (() => {
  try {
    return window.self !== window.top
      && document.referrer
      && /(?:^|\.)trashie\.io$/.test(new URL(document.referrer).hostname);
  } catch (error) {
    return false;
  }
})();

if (window.self === window.top || isFirstPartyEmbed) {
  TagManager.initialize({
    gtmId: TRASHIE_GA_CONTAINER_ID,
  });
}

const MagellanRouteTracker = () => {
  const location = useLocation();
  const isFirstRouteRender = useRef(true);

  useEffect(() => {
    if (isFirstRouteRender.current) {
      isFirstRouteRender.current = false;
      return;
    }

    trackMagellanPageView();
  }, [location.pathname, location.search]);

  return null;
};

const App = () => {
  useExternalScript(GOOGLE_TAG_MANAGER_URL);
  useScript(gaScript);
  useScript(northbeamScript);
  useScript(equalwebScript);
  useScript(redditPixelScript);
  useScript(null, googlePlacesApi);
  useScript(tiktokPixelScript);
  useScript(magellanPixelScript);

  useEffect(() => {
    setDocumentTitle();
    setDocumentFavicon();
  }, []);

  return (
    <FirebaseProvider>
      <AlertProvider>
        <UtmProvider>
          <Router history={history}>
            <Switch>
              {isTrashieApp() ? (
                <PostHogProvider
                  apiKey={isProd() ? (
                    TRASHIE_POSTHOG_API_KEY.PRODUCTION
                  ) : (
                    TRASHIE_POSTHOG_API_KEY.STAGING
                  )}
                  options={{
                    api_host: TRASHIE_POSTHOG_API_HOST,
                    before_send: createBeforeSendHook(),
                  }}
                >
                  <MagellanRouteTracker />
                  <AnalyticsInitializer>
                    {!isProd() && <EnvironmentBanner />}
                    <MaintenanceBanner />
                    <Container>
                      <Route exact path={ROUTE_HOME}>
                        <Redirect to={ROUTE_DASHBOARD} />
                      </Route>
                      <Route exact path="/profile">
                        <Redirect to={ROUTE_PROFILE} />
                      </Route>
                      <TrashiePublicRoute
                        path={ROUTE_REDEEM}
                        exact
                        render={(location) => (
                          <Redirect to={`${ROUTE_REGISTER}${location.search}`} />
                        )}
                      />
                      <TrashiePublicRoute
                        path={ROUTE_SIGNUP}
                        exact
                        render={(location) => (
                          <Redirect to={`${ROUTE_LOGIN}${location.search}`} />
                        )}
                      />
                      <TrashiePublicRoute
                        path={ROUTE_REGISTER}
                        exact
                        handleRedirect={false}
                        component={RedemptionLandingPage}
                      />
                      <TrashiePublicRoute
                        path={ROUTE_SUBSCRIPTION}
                        exact
                        render={(location) => (
                          <Redirect
                            to={{
                              pathname: ROUTE_UNLIMITED,
                              search: location.search,
                              hash: location.hash,
                              ...(location.state != null ? { state: location.state } : {}),
                            }}
                          />
                        )}
                      />
                      <TrashiePublicRoute
                        path={ROUTE_UNLIMITED}
                        exact
                        handleRedirect={false}
                        component={Subscription}
                      />
                      <TrashiePublicRoute
                        path={ROUTE_TRASHIE_UNLIMITED}
                        exact
                        handleRedirect={false}
                        component={Subscription}
                      />
                      <TrashiePublicRoute
                        path={ROUTE_ONE_TIME_ORDER}
                        exact
                        handleRedirect={false}
                        component={OneTimeOrder}
                      />
                      <TrashiePublicRoute
                        path={ROUTE_SHARE_STORE_DEAL_ID}
                        exact
                        handleRedirect={false}
                        component={ShareDeal}
                      />
                      <TrashiePublicRoute
                        path={ROUTE_SHARE_STORE_ID}
                        exact
                        handleRedirect={false}
                        component={ShareDeal}
                      />
                      <TrashiePublicRoute
                        path={ROUTE_MOBILE_LOGIN}
                        exact
                        handleRedirect={false}
                        component={MobileRedirect}
                      />
                      <TrashiePublicRoute
                        path={ROUTE_MOBILE_VERIFY_EMAIL}
                        exact
                        handleRedirect={false}
                        component={MobileRedirect}
                      />
                      <TrashiePublicRoute
                        path={ROUTE_WELCOME_BACK}
                        exact
                        handleRedirect={false}
                        component={MobileRedirect}
                      />
                    </Container>
                    <TrashieContainerPublicCentered>
                      <TrashiePublicRoute
                        path={ROUTE_LOGIN}
                        exact
                        handleRedirect={false}
                        component={TrashieLogin}
                      />
                    </TrashieContainerPublicCentered>
                    <TrashieContainerPublic>
                      <TrashiePublicRoute
                        path={ROUTE_EMBED_LOGIN}
                        exact
                        handleRedirect={false}
                        component={TrashieEmbedLogin}
                      />
                      <TrashiePublicRoute
                        path={ROUTE_EMBED_BROWN_FORM}
                        exact
                        handleRedirect={false}
                        component={BrownForm}
                      />
                    </TrashieContainerPublic>
                    <TrashieContainer>
                      <TrashiePrivateRoute
                        path={ROUTE_LOGOUT}
                        exact
                        component={TrashieLogout}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_SETTINGS}
                        exact
                        component={TrashieSettings}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_PROFILE}
                        exact
                        component={TrashieProfile}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_ADDRESSES}
                        exact
                        component={TrashieAddresses}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_REWARDS_V2_STORE_ID}
                        exact
                        component={TrashieReward}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_REWARDS_V2_STORE_REWARD_ID}
                        exact
                        component={TrashieReward}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_REWARDS_V2_LIST}
                        exact
                        component={TrashieRewardsV2List}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_REWARDS_V2}
                        exact
                        component={TrashieDashboardRewardsV2}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_GIFT_CARDS_V2_BRAND_ID}
                        exact
                        component={TrashieGiftCard}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_GIFT_CARDS_V2_LIST}
                        exact
                        component={TrashieGiftCardsV2List}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_GIFT_CARDS_V2}
                        exact
                        component={TrashieDashboardGiftCardsV2}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_OFFERS_V2}
                        exact
                        component={TrashieOffersV2}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_DASHBOARD}
                        exact
                        component={TrashieDashboardNew}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_DASHBOARD_REWARDS}
                        exact
                        component={TrashieDashboardRewards}
                      />
                      <TrashiePrivateRoute path={ROUTE_WALLET} exact component={TrashieWallet} />
                      <TrashiePrivateRoute
                        path={ROUTE_TAKE_BACK_BAGS}
                        exact
                        component={TrashieTakeBackBags}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_TAKE_BACK_BAGS_DETAILS}
                        exact
                        component={TrashieTakeBackBagsDetails}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_MY_ACCOUNT}
                        exact
                        component={TrashieMyAccount}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_WEB_NOTIFICATIONS_INBOX}
                        exact
                        component={NotificationsInboxWebRedirect}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_WEB_NOTIFICATIONS_INBOX_ACCOUNT}
                        exact
                        component={NotificationsInboxWebRedirect}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_REWARD_ID}
                        exact
                        component={TrashieOldReward}
                      />
                      {/* Old rewards URL support - Start */}
                      <TrashiePrivateRoute
                        path={ROUTE_REWARDS_OLD_STORE_ID}
                        exact
                        component={TrashieReward}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_REWARDS_OLD_STORE_REWARD_ID}
                        exact
                        component={TrashieReward}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_REWARD_OLD_INFO}
                        exact
                        component={TrashieRewardInfo}
                      />
                      {/* Old rewards URL support - End */}
                      <TrashiePrivateRoute
                        path={ROUTE_REWARDS_STORE_ID}
                        exact
                        component={TrashieReward}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_REWARDS_STORE_REWARD_ID}
                        exact
                        component={TrashieReward}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_REWARD_INFO}
                        exact
                        component={TrashieRewardInfo}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_DEAL_INFO}
                        exact
                        component={TrashieRewardInfo}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_REWARDS}
                        exact
                        component={TrashieRewards}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_TRANSACTIONS}
                        exact
                        component={TrashieTransactions}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_OFFERS}
                        exact
                        component={TrashieOffers}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_DEALS}
                        exact
                        component={TrashieDeals}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_DEAL_STORE_ID}
                        exact
                        component={TrashieReward}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_DEAL_STORE_REWARD_ID}
                        exact
                        component={TrashieReward}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_GIFT_CARDS}
                        exact
                        component={TrashieGiftCards}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_GIFT_CARDS_BRAND_ID}
                        exact
                        component={TrashieGiftCard}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_GIFT_CARD_INFO}
                        exact
                        component={TrashieGiftCardInfo}
                      />
                      <TrashieMembershipContainer>
                        <TrashiePrivateRoute
                          path={ROUTE_MEMBERSHIP}
                          exact
                          component={TrashieMembership}
                        />
                        <TrashiePrivateRoute
                          path={ROUTE_MANAGE_MEMBERSHIP}
                          exact
                          component={TrashieManageMembership}
                        />
                        <TrashiePrivateRoute
                          path={ROUTE_MEMBERSHIP_ORDERS}
                          exact
                          component={TrashieMembershipOrders}
                        />
                        <TrashiePrivateRoute
                          path={ROUTE_MEMBERSHIP_ADDRESSES}
                          exact
                          component={MembershipAddresses}
                        />
                        <TrashiePrivateRoute
                          path={ROUTE_MEMBERSHIP_PAYMENT_METHOD}
                          exact
                          component={MembershipPaymentMethod}
                        />
                        <TrashiePrivateRoute
                          path={ROUTE_MEMBERSHIP_CANCEL}
                          exact
                          component={TrashieMembershipCancel}
                        />
                      </TrashieMembershipContainer>
                    </TrashieContainer>
                  </AnalyticsInitializer>
                </PostHogProvider>
              ) : (
                <PublicRoute
                  path="*"
                  render={(location) => {
                    window.location.href = (window.location.pathname).includes(ROUTE_REDEEM) ?
                      `${getConfig('BASE_URL_TRASHIE')}${ROUTE_REGISTER}${location.search}` :
                      `${getConfig('BASE_URL_TRASHIE')}`;
                  }}
                />
              )}
            </Switch>
          </Router>
        </UtmProvider>
      </AlertProvider>
      <div id="recaptcha-container" />
    </FirebaseProvider>
  );
};

export default App;
